const MAX_STACK_SIZE = 50;

const UNDO_STACK_KEY_PREFIX = 'undo_redo_stack_';

/**
 * Removes every undo/redo stack that older builds persisted to localStorage.
 * The stack now lives in memory only, so these entries are dead weight that
 * crowds other keys out of the storage quota. Call once on app init.
 */
export const removePersistedUndoStacks = () => {
  const keysToRemove = [];
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key && key.startsWith(UNDO_STACK_KEY_PREFIX)) keysToRemove.push(key);
  }
  keysToRemove.forEach((k) => localStorage.removeItem(k));
};

/**
 * Undo/redo stack for project-level changes. Each entry is one of:
 *
 *   Manual: { before, after, meta: { origin: 'manual', packetId } }
 *     - before: full cleaned packet snapshot before the edit
 *     - after:  full cleaned packet snapshot after the edit
 *     - Caller applies entry.before on undo, entry.after on redo.
 *
 *   Chat:   { meta: { origin: 'chat', postVersionNumber, affectedPacketIds } }
 *     - No before/after — caller restores from backend by version number.
 *     - Caller uses postVersionNumber-1 on undo, postVersionNumber on redo.
 *
 * currentIndex starts at -1 (empty stack). The first pushed entry is at index 0
 * and is immediately undoable (canUndo returns true when currentIndex >= 0).
 *
 * Held in memory only, so a reload starts with an empty stack. Older state is
 * recoverable from the project's version history.
 */
class ProjectChangesStack {
  constructor(projectId) {
    this.projectId = projectId;
    this.isDisabled = false;
    this.timeoutId = null;
    this.items = [];
    this.currentIndex = -1;
    this.checkpoints = new Map();
  }

  length() {
    return this.items.length;
  }

  // Alias used by some callers
  get undoRedoStackLength() {
    return this.items.length;
  }

  enable() {
    this.isDisabled = false;
  }

  disable() {
    this.isDisabled = true;
  }

  peek() {
    return this.items[this.currentIndex];
  }

  // Read current stack position (zero-based index into items)
  currentIndexValue() {
    return this.currentIndex;
  }

  // Return the meta object for an entry at an arbitrary stack index
  getMetaAt(index) {
    return this.items[index]?.meta;
  }

  // Add a manual or chat entry to the top of the stack.
  // Manual entry shape: { before, after, meta: { origin: 'manual', packetId } }
  // Chat entry shape:   { meta: { origin: 'chat', postVersionNumber, affectedPacketIds } }
  push(entry) {
    if (this.isDisabled) return;

    // Dedup: skip ONLY when BOTH the new entry AND the previous entry are manual
    // (origin === 'manual') AND they share the same packetId AND their `after`
    // snapshots are identical. Chat entries are NEVER deduped.
    try {
      if (this.currentIndex >= 0) {
        const prev = this.items[this.currentIndex];
        const newIsManual = entry.meta?.origin === 'manual';
        const prevIsManual = prev?.meta?.origin === 'manual';
        if (newIsManual && prevIsManual) {
          const samePacketId = entry.meta?.packetId === prev?.meta?.packetId;
          const sameAfter = JSON.stringify(entry.after) === JSON.stringify(prev.after);
          if (samePacketId && sameAfter) return;
        }
      }
    } catch (error) {
      console.error('Stack error: Error checking if item is the same as the current item:', error);
      return;
    }

    // Clean up checkpoints that will be invalidated by the splice
    for (const [id, index] of this.checkpoints.entries()) {
      if (index > this.currentIndex) {
        this.checkpoints.delete(id);
      }
    }

    // Clear any future redo items
    this.items.splice(this.currentIndex + 1);

    // Enforce cap: drop oldest entry and adjust indices
    if (this.items.length >= MAX_STACK_SIZE) {
      this.items.shift();
      for (const [id, index] of Array.from(this.checkpoints.entries())) {
        if (index <= 0) {
          this.checkpoints.delete(id);
        } else {
          this.checkpoints.set(id, index - 1);
        }
      }
    } else {
      this.currentIndex++;
    }

    this.items.push(entry);
  }

  // True once there is at least one entry to undo (currentIndex >= 0).
  canUndo() {
    return this.currentIndex >= 0;
  }

  canRedo() {
    return this.currentIndex < this.items.length - 1;
  }

  // Returns the entry being undone (capture BEFORE decrementing), then moves
  // currentIndex back by one. Caller applies entry.before for manual entries or
  // restores postVersionNumber-1 for chat entries.
  undo() {
    if (!this.canUndo()) return null;

    if (this.timeoutId) {
      clearTimeout(this.timeoutId);
    }

    this.isDisabled = true;

    const entry = this.items[this.currentIndex];
    this.currentIndex--;

    this.timeoutId = setTimeout(() => {
      this.isDisabled = false;
      this.timeoutId = null;
    }, 250);

    return entry;
  }

  // Returns the entry being redone (after incrementing currentIndex). Caller
  // applies entry.after for manual entries or restores postVersionNumber for chat.
  redo() {
    if (!this.canRedo()) return null;

    if (this.timeoutId) {
      clearTimeout(this.timeoutId);
    }

    this.isDisabled = true;
    this.currentIndex++;

    this.timeoutId = setTimeout(() => {
      this.isDisabled = false;
      this.timeoutId = null;
    }, 250);

    return this.items[this.currentIndex];
  }

  // Create a checkpoint at current position
  createCheckpoint(checkpointId) {
    this.checkpoints.set(checkpointId, this.currentIndex);
    return checkpointId;
  }

  // Check if a checkpoint exists in this stack
  hasCheckpoint(checkpointId) {
    return this.checkpoints.has(checkpointId);
  }

  // Return the stored stack index for a checkpoint (number, or undefined if absent)
  getCheckpointIndex(checkpointId) {
    return this.checkpoints.get(checkpointId);
  }

  // A checkpoint is valid if it exists, its index is >= -1 (pre-everything is valid),
  // and its index is before the current position.
  isCheckpointValid(checkpointId) {
    const checkpointIndex = this.checkpoints.get(checkpointId);
    return (
      checkpointIndex !== undefined && checkpointIndex >= -1 && checkpointIndex < this.currentIndex
    );
  }

  // Undo to a specific checkpoint — returns the entry at that position.
  // An index of -1 is allowed (turn that started on an empty stack).
  undoToCheckpoint(checkpointId) {
    const targetIndex = this.checkpoints.get(checkpointId);
    if (targetIndex === undefined || targetIndex < -1) return null;

    if (this.timeoutId) {
      clearTimeout(this.timeoutId);
    }

    this.isDisabled = true;
    this.currentIndex = targetIndex;

    this.timeoutId = setTimeout(() => {
      this.isDisabled = false;
      this.timeoutId = null;
    }, 250);
    return this.items[targetIndex];
  }

  // Clean up invalid checkpoints (optional, for memory management)
  cleanupCheckpoints() {
    for (const [id, index] of this.checkpoints.entries()) {
      if (index > this.currentIndex || index >= this.items.length) {
        this.checkpoints.delete(id);
      }
    }
  }

  // Clear the stack
  clear() {
    this.items = [];
    this.currentIndex = -1;
    this.checkpoints.clear();
    if (this.timeoutId) {
      clearTimeout(this.timeoutId);
      this.timeoutId = null;
    }
  }
}

export default ProjectChangesStack;
